import React from 'react';
import Modal from '@/components/modal/Modal';
import { Col, Row } from '@/lib/utils';
import { DemiLightText, MediumText, RegularText } from '@/lib/Text';
import { colors } from '@/lib/colors';
import { overlay } from 'overlay-kit';
import ModalAlertOneButton from '@/components/modal/ModalAlertOneButton';
import {
    CoachMemberSessionRow,
    usePutCoachMemberSessionOffsets,
} from '@/features/coachMemberSessionOffset';
import { isMemberRecordReadOnly } from '@/features/publicMember/types';

type Props = {
    onClose: () => void;
    centerId: string;
    coachId: string;
    coachName: string;
    members: CoachMemberSessionRow[];
};

const ModalSessionOffsetEdit = ({ onClose, centerId, coachId, coachName, members }: Props) => {
    const [editedSessionCounts, setEditedSessionCounts] = React.useState<Record<string, number>>({});
    const { mutateAsync: putSessionOffsetsAsync, isLoading } = usePutCoachMemberSessionOffsets();

    const hasChanges = React.useMemo(
        () =>
            members.some((m) => {
                if (isMemberRecordReadOnly(m)) return false;
                const edited = editedSessionCounts[m.memberId];
                return edited !== undefined && edited !== m.cumulativeSessionCount;
            }),
        [editedSessionCounts, members],
    );

    const onSave = async () => {
        if (!centerId || !coachId) return;
        const entries = members
            .filter((m) => {
                if (isMemberRecordReadOnly(m)) return false;
                const edited = editedSessionCounts[m.memberId];
                return edited !== undefined && edited !== m.cumulativeSessionCount;
            })
            .map((m) => ({
                memberId: m.memberId,
                cumulativeSessionCount: editedSessionCounts[m.memberId],
            }));
        if (entries.length === 0) return;
        const invalid = entries.find(
            (e) => !Number.isInteger(e.cumulativeSessionCount) || e.cumulativeSessionCount < 0,
        );
        if (invalid) {
            overlay.open(({ unmount }) => (
                <ModalAlertOneButton
                    title="입력값 오류"
                    content="누적 회차는 0 이상의 정수로 입력해주세요."
                    isError
                    buttonContent="확인"
                    callBack={unmount}
                    isTwoButton={false}
                    twoButtonContent={null}
                    twoButtonCallBack={null}
                    alertStateReset={unmount}
                />
            ));
            return;
        }
        try {
            await putSessionOffsetsAsync({ centerId, coachId, body: { entries } });
            setEditedSessionCounts({});
            overlay.open(({ unmount }) => (
                <ModalAlertOneButton
                    title="저장 완료"
                    content="회원별 누적 회차가 저장되었어요. 다음 정산부터 바로 반영됩니다."
                    buttonContent="확인"
                    callBack={() => {
                        unmount();
                        onClose();
                    }}
                    isTwoButton={false}
                    twoButtonContent={null}
                    twoButtonCallBack={null}
                    alertStateReset={unmount}
                />
            ));
        } catch (error) {
            console.error(error);
            overlay.open(({ unmount }) => (
                <ModalAlertOneButton
                    title="저장에 실패했어요"
                    content="잠시 후 다시 시도해주세요."
                    isError
                    buttonContent="확인"
                    callBack={unmount}
                    isTwoButton={false}
                    twoButtonContent={null}
                    twoButtonCallBack={null}
                    alertStateReset={unmount}
                />
            ));
        }
    };

    return (
        <Modal
            onClose={onClose}
            whiteViewWidth={620}
            whiteViewHeight={720}
            borderRadius={20}
            isCloseButton={false}
            isWhiteOverFlowScroll={false}
        >
            <Col className="px-[32px] pt-[24px] pb-[24px] h-full">
                {/* 제목 */}
                <MediumText fontSize={22} lineHeight={30} color="#3D4244" letterSpacing={-0.5}>
                    회원별 누적 회차 보정
                </MediumText>
                <DemiLightText fontSize={14} lineHeight={20} color="#6B7280" className="mt-[4px]">
                    {coachName} 강사
                </DemiLightText>

                {/* 설명 박스 */}
                <Col className="mt-[20px] bg-[#F6F8FA] rounded-[12px] px-[16px] py-[14px]">
                    <Row className="items-center mb-[8px]">
                        <DemiLightText fontSize={14} lineHeight={20} color="#3D4244" letterSpacing={-0.3}>
                            <b>이 화면은 무엇인가요?</b>
                        </DemiLightText>
                    </Row>
                    <DemiLightText fontSize={13} lineHeight={20} color="#5A6370" letterSpacing={-0.3}>
                        세션별 정산은 <b>강사와 해당회원이 지금까지 진행한 총 수업 회차</b>에 따라 정산 요율이
                        달라집니다. 예: 1~49회차는 50%, 50회차부터는 55%.
                    </DemiLightText>
                    <DemiLightText
                        fontSize={13}
                        lineHeight={20}
                        color="#5A6370"
                        letterSpacing={-0.3}
                        className="mt-[10px]"
                    >
                        하지만 <b>과거 수업을 라포에 모두 등록해 두지 않은 경우</b>, 라포가 세는 회차가 실제
                        보다 적어 잘못된 구간의 요율이 적용될 수 있어요. 이 화면에서 회원별 <b>실제 현재 누적
                        회차를 직접 입력</b>해 바로잡을 수 있습니다.
                    </DemiLightText>
                    <DemiLightText
                        fontSize={13}
                        lineHeight={20}
                        color="#5A6370"
                        letterSpacing={-0.3}
                        className="mt-[10px]"
                    >
                        ✓ 한 번 저장하면, 이후 라포에 새 수업이 등록될 때마다 <b>자동으로 +1씩 누적</b>되므로
                        매달 다시 입력할 필요가 없어요.
                    </DemiLightText>
                    <DemiLightText
                        fontSize={13}
                        lineHeight={20}
                        color="#5A6370"
                        letterSpacing={-0.3}
                        className="mt-[4px]"
                    >
                        ✓ "라포에 등록된 수업 수"는 현재 라포에 등록된 수업만 센 값이고, "누적 회차"가 실제
                        정산에 쓰이는 값입니다.
                    </DemiLightText>
                </Col>

                {/* 테이블 */}
                <Col className="mt-[20px] flex-1 min-h-0">
                    <Row className="px-[12px] py-[10px] bg-[#F6F8FA] rounded-t-[8px] border border-solid border-[#E6E8EB]">
                        <div className="flex-1">
                            <DemiLightText fontSize={13} letterSpacing={-0.3} color="#6B7280">
                                회원
                            </DemiLightText>
                        </div>
                        <div className="w-[140px] text-right">
                            <DemiLightText fontSize={13} letterSpacing={-0.3} color="#6B7280">
                                라포에 등록된 수업 수
                            </DemiLightText>
                        </div>
                        <div className="w-[160px] text-right">
                            <DemiLightText fontSize={13} letterSpacing={-0.3} color="#6B7280">
                                실제 현재 누적 회차
                            </DemiLightText>
                        </div>
                    </Row>
                    <Col className="overflow-y-auto border-x border-b border-solid border-[#E6E8EB] rounded-b-[8px] flex-1">
                        {members.length === 0 ? (
                            <Col className="items-center justify-center py-[40px]">
                                <DemiLightText fontSize={14} letterSpacing={-0.3} color="#6B7280">
                                    보정할 회원이 없습니다.
                                </DemiLightText>
                            </Col>
                        ) : (
                            members.map((m) => {
                                const isReadOnly = isMemberRecordReadOnly(m);
                                const editedValue = editedSessionCounts[m.memberId];
                                const displayValue =
                                    editedValue !== undefined ? editedValue : m.cumulativeSessionCount;
                                const isChanged =
                                    editedValue !== undefined && editedValue !== m.cumulativeSessionCount;
                                return (
                                    <Row
                                        key={m.memberId}
                                        className="px-[12px] py-[10px] items-center border-t border-solid border-[#E6E8EB] first:border-t-0"
                                    >
                                        <div className="flex-1">
                                            <DemiLightText fontSize={14} letterSpacing={-0.3} color={colors.Text_1}>
                                                {m.memberName}
                                            </DemiLightText>
                                            {isReadOnly && (
                                                <span className="inline-flex mt-[4px] rounded-full border border-solid border-[#FCD34D] bg-[#FFFBEB] px-[7px] py-[2px] text-[13px] font-bold text-[#92400E]">
                                                    탈퇴 · 조회 전용
                                                </span>
                                            )}
                                        </div>
                                        <div className="w-[140px] text-right">
                                            <DemiLightText fontSize={14} letterSpacing={-0.3} color="#6B7280">
                                                {m.rappoCount}회
                                            </DemiLightText>
                                        </div>
                                        <div className="w-[160px] flex justify-end items-center">
                                            <input
                                                type="number"
                                                min={0}
                                                step={1}
                                                value={displayValue}
                                                disabled={isReadOnly}
                                                onChange={(e) => {
                                                    const raw = e.target.value;
                                                    if (raw === '') {
                                                        setEditedSessionCounts((prev) => ({
                                                            ...prev,
                                                            [m.memberId]: 0,
                                                        }));
                                                        return;
                                                    }
                                                    const parsed = parseInt(raw, 10);
                                                    if (Number.isNaN(parsed)) return;
                                                    setEditedSessionCounts((prev) => ({
                                                        ...prev,
                                                        [m.memberId]: parsed,
                                                    }));
                                                }}
                                                className={`w-[90px] h-[32px] text-right px-[10px] rounded-[6px] border border-solid outline-none ${
                                                    isReadOnly
                                                        ? 'border-[#E6E8EB] bg-[#F6F8FA] text-[#6B7280] cursor-not-allowed'
                                                        : isChanged
                                                        ? 'border-[#3D73DD] text-[#3D73DD]'
                                                        : 'border-[#E6E8EB] text-[#3D4244]'
                                                }`}
                                                style={{ fontSize: 14 }}
                                            />
                                            <DemiLightText
                                                fontSize={13}
                                                letterSpacing={-0.3}
                                                color="#6B7280"
                                                className="ml-[6px]"
                                            >
                                                회
                                            </DemiLightText>
                                        </div>
                                    </Row>
                                );
                            })
                        )}
                    </Col>
                </Col>

                {/* 하단 버튼 */}
                <Row className="gap-[12px] justify-end mt-[20px]">
                    <div
                        className="w-[110px] h-[44px] bg-[#F4F5F7] rounded-[12px] flex items-center justify-center cursor-pointer"
                        onClick={onClose}
                    >
                        <RegularText fontSize={16} color="#3D4244">
                            닫기
                        </RegularText>
                    </div>
                    <div
                        className={`w-[130px] h-[44px] rounded-[12px] flex items-center justify-center ${
                            hasChanges && !isLoading
                                ? 'bg-[#3D73DD] cursor-pointer'
                                : 'bg-[#E6E8EB] cursor-not-allowed'
                        }`}
                        onClick={() => {
                            if (!hasChanges || isLoading) return;
                            onSave();
                        }}
                    >
                        <RegularText
                            fontSize={16}
                            color={hasChanges && !isLoading ? '#FFFFFF' : '#6B7280'}
                        >
                            {isLoading ? '저장 중...' : '저장'}
                        </RegularText>
                    </div>
                </Row>
            </Col>
        </Modal>
    );
};

export default ModalSessionOffsetEdit;
